(() => {
  'use strict';

  const SCHEMA = 'market-search-index/v1';
  const CATALOG_SCHEMA = 'market-search-index/v2';
  const MAX_ITEMS = 20000;
  const MAX_BYTES = 2 * 1024 * 1024;
  const loads = new Map();
  let instanceCount = 0;

  function normalize(value) {
    return typeof value === 'string' ? value.normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '') : '';
  }

  function indexUrl(value, origin) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, origin);
      if (url.protocol !== 'https:' || url.username || url.password || url.hash) return null;
      if (url.origin !== origin && url.origin !== 'https://cdn.shopify.com') return null;
      if (!url.pathname.endsWith('.json')) return null;
      return url.href;
    } catch (_error) {
      return null;
    }
  }

  function pageUrl(item, origin) {
    if (item?.is_page_available !== true || item.page_type !== 'ref' || item.search_index_state !== 'index') return null;
    if (typeof item.canonical_url !== 'string' || !item.canonical_url) return null;
    try {
      const url = new URL(item.canonical_url, origin);
      if (url.origin !== origin || url.username || url.password || url.search || url.hash) return null;
      if (!/^\/collections\/ref-[a-z0-9]+(?:-[a-z0-9]+)*$/.test(url.pathname)) return null;
      return url.pathname;
    } catch (_error) {
      return null;
    }
  }

  function parseIndex(data, origin, now = Date.now()) {
    const catalogMode = data?.schema_version === CATALOG_SCHEMA;
    if ((!catalogMode && data?.schema_version !== SCHEMA) || typeof data.snapshot_id !== 'string' || !data.snapshot_id) return null;
    if (catalogMode && (data.refresh_policy !== 'on_catalog_change' || data.content_kind !== 'reference_identity' || data.expires_at !== null)) return null;
    if (!Array.isArray(data.items) || data.items.length > MAX_ITEMS || data.item_count !== data.items.length) return null;
    const generated = Date.parse(data.generated_at);
    const verified = Date.parse(data.links_verified_at);
    const expires = catalogMode ? Infinity : Date.parse(data.expires_at);
    if (![generated, verified].every(Number.isFinite) || (!catalogMode && !Number.isFinite(expires))) return null;
    if (generated > now + 300000 || verified > now + 300000 || expires <= now || expires <= generated || expires <= verified) return null;
    const ids = new Set();
    const urls = new Set();
    const items = [];
    for (const item of data.items) {
      const url = pageUrl(item, origin);
      if (!url || typeof item.reference_id !== 'string' || !item.reference_id || item.reference_id.length > 200) continue;
      if (typeof item.canonical_ref !== 'string' || !item.canonical_ref.trim() || item.canonical_ref.length > 200) continue;
      if (ids.has(item.reference_id) || urls.has(url)) continue;
      const fields = [item.brand, item.brand_en, item.canonical_ref].filter((value) => typeof value === 'string');
      if (fields.some((value) => value.length > 300)) continue;
      ids.add(item.reference_id);
      urls.add(url);
      items.push({
        referenceId: item.reference_id,
        canonicalRef: item.canonical_ref,
        title: [item.brand || item.brand_en, item.canonical_ref].filter((value) => typeof value === 'string' && value.trim()).join(' '),
        url,
        ref: normalize(item.canonical_ref),
        text: fields.map(normalize).join(' '),
      });
    }
    return { items, expires, snapshotId: data.snapshot_id };
  }

  function search(index, query, now = Date.now()) {
    if (!index || index.expires <= now || typeof query !== 'string' || query.length > 100) return [];
    const tokens = query.normalize('NFKC').trim().split(/\s+/).map(normalize).filter(Boolean);
    if (!tokens.length || tokens.join('').length < 2) return [];
    const ref = normalize(query);
    const rank = (item) => item.ref === ref ? 0 : item.ref.startsWith(ref) ? 1 : 2;
    return index.items.filter((item) => tokens.every((token) => item.text.includes(token)))
      .sort((a, b) => rank(a) - rank(b) || a.canonicalRef.localeCompare(b.canonicalRef, 'en') || a.referenceId.localeCompare(b.referenceId, 'en'))
      .slice(0, 10);
  }

  function loadIndex(url, origin) {
    if (loads.has(url)) return loads.get(url);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2500);
    const promise = fetch(url, { signal: controller.signal, credentials: 'omit', redirect: 'error', headers: { Accept: 'application/json' } })
      .then(async (response) => {
        if (!response.ok || Number(response.headers.get('content-length')) > MAX_BYTES) return null;
        const text = await readIndexText(response);
        if (text === null) return null;
        return parseIndex(JSON.parse(text), origin);
      })
      .catch(() => null)
      .finally(() => clearTimeout(timeout));
    // One attempt per page load; an unavailable index must not add a request per keystroke.
    loads.set(url, promise);
    return promise;
  }

  async function readIndexText(response) {
    if (!response.body?.getReader) {
      const text = await response.text();
      return new TextEncoder().encode(text).byteLength <= MAX_BYTES ? text : null;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let bytes = 0;
    let text = '';
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) return text + decoder.decode();
        bytes += value.byteLength;
        if (bytes > MAX_BYTES) {
          await reader.cancel();
          return null;
        }
        text += decoder.decode(value, { stream: true });
      }
    } finally {
      reader.releaseLock();
    }
  }

  class MarketSearch {
    constructor(host) {
      this.host = host;
      this.origin = window.location.origin;
      this.url = host.dataset.marketSearchEnabled === 'true' ? indexUrl(host.dataset.marketSearchIndexUrl, this.origin) : null;
      this.label = host.dataset.marketSearchHeading || '';
      this.prefix = `ts-market-search-${++instanceCount}`;
      this.sequence = 0;
      this.items = [];
      this.query = '';
    }

    reset() {
      this.sequence += 1;
      this.items = [];
      this.query = '';
      this.removeGroup();
    }

    removeGroup() {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
      const group = this.host.querySelector('[data-ts-market-search-group]');
      if (group?.querySelector('[aria-selected="true"]')) this.host.input?.setAttribute('aria-activedescendant', '');
      group?.remove();
      const standalone = this.host.querySelector('[data-ts-market-search-root]');
      if (standalone && !standalone.children.length) {
        standalone.remove();
        this.host.removeAttribute('results');
      }
    }

    async update(query) {
      this.reset();
      if (!this.url || !this.label || !query || query.length > 100 || normalize(query).length < 2) return;
      this.query = query;
      const sequence = this.sequence;
      const index = await loadIndex(this.url, this.origin);
      if (sequence !== this.sequence || query !== this.host.getQuery() || this.host.isComposing) return;
      this.items = search(index, query);
      this.expires = index?.expires || 0;
      this.render();
    }

    render({ preserveSelection = false } = {}) {
      const valid = this.items.length && this.expires > Date.now() && this.query === this.host.getQuery() && !this.host.isComposing;
      // An unchanged fallback must preserve the option already selected with the keyboard.
      if (valid && preserveSelection && this.host.querySelector('[data-ts-market-search-group]')) return true;
      this.removeGroup();
      if (!valid) return false;
      let root = this.host.querySelector('#predictive-search-results');
      if (!root) {
        root = document.createElement('div');
        root.id = 'predictive-search-results';
        root.setAttribute('role', 'listbox');
        root.setAttribute('data-ts-market-search-root', '');
        this.host.predictiveSearchResults.appendChild(root);
      }
      const presentUrls = new Set(Array.from(root.querySelectorAll('a[href]')).map((link) => {
        try { return new URL(link.getAttribute('href'), this.origin).pathname; } catch (_error) { return ''; }
      }));
      const items = this.items.filter((item) => !presentUrls.has(item.url));
      if (!items.length) return false;
      const group = document.createElement('div');
      group.setAttribute('data-ts-market-search-group', '');
      const heading = document.createElement('h2');
      heading.id = `${this.prefix}-heading`;
      heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
      heading.textContent = this.label;
      const list = document.createElement('ul');
      list.className = 'predictive-search__results-list list-unstyled';
      list.setAttribute('role', 'group');
      list.setAttribute('aria-labelledby', heading.id);
      items.forEach((item, index) => {
        const row = document.createElement('li');
        row.id = `${this.prefix}-option-${index}`;
        row.className = 'predictive-search__list-item';
        row.setAttribute('role', 'option');
        row.setAttribute('aria-selected', 'false');
        const link = document.createElement('a');
        link.href = item.url;
        link.tabIndex = -1;
        link.className = 'predictive-search__item link link--text';
        link.setAttribute('data-ts-verified-entity-link', '');
        link.setAttribute('data-reference-id', item.referenceId);
        link.addEventListener('click', (event) => {
          if (this.expires <= Date.now()) {
            event.preventDefault();
            this.reset();
          }
        });
        const content = document.createElement('div');
        content.className = 'predictive-search__item-content predictive-search__item-content--centered';
        const title = document.createElement('p');
        title.className = 'predictive-search__item-heading h5';
        title.textContent = item.title;
        content.appendChild(title);
        link.appendChild(content);
        row.appendChild(link);
        list.appendChild(row);
      });
      group.appendChild(heading);
      group.appendChild(list);
      root.insertBefore(group, root.querySelector('.predictive-search__search-for-button'));
      if (Number.isFinite(this.expires)) {
        this.expiryTimer = setTimeout(() => {
          this.removeGroup();
          if (!this.host.querySelector('#predictive-search-results')) this.host.close();
        }, Math.min(this.expires - Date.now(), 2147483647));
      }
      this.host.setAttribute('results', 'true');
      this.host.removeAttribute('loading');
      this.host.removeLoadingPlaceholder();
      if (this.host.isOpen || this.host.contains(document.activeElement)) {
        this.host.open();
        this.host.setLiveRegionText(`${this.label}: ${items.length}`);
      }
      return true;
    }
  }

  window.TimeseekMarketSearch = { MarketSearch, normalize, indexUrl, pageUrl, parseIndex, search };
})();
